import { useEffect, useState } from 'react';
import { IconProps as PhosphorIconProps } from '@phosphor-icons/react';
import parse from 'html-react-parser';
import ky from 'ky';
import qs from 'qs';

import { IconApiResponse } from '@tmh-website/shared/cms';

import * as TMHIcons from './icons';
import { Car, Installation, Search, User } from './icons';
import { IconId } from './types';

interface PhosphorIconWrapperProps extends PhosphorIconProps {
  iconId: IconId;
}

/**
 * Get a rendered Phosphor icon from the server
 */

const PhosphorIconWrapper = ({ iconId, color, mirrored, size = 24, weight, ...props }: PhosphorIconWrapperProps) => {
  const [icon, setIcon] = useState<IconApiResponse['data'] | null>(null);

  useEffect(() => {
    // declare the data fetching function
    async function getIcon(iconId: IconId) {
      const response = await ky
        // The env variable name is different on the magento-header
        .get(`${process.env.NEXT_PUBLIC_ORIGIN ?? process.env.NX_NEXT_PUBLIC_ORIGIN}/web-v2/api/icons`, {
          searchParams: qs.stringify({
            iconId: iconId,
            props: { color, mirrored, size, weight, ...props },
          }),
        })
        .json<IconApiResponse>()
        .catch(e => console.error('Error fetching icon', e));
      if (response && response?.success && response?.data) {
        setIcon(response.data);
      }
    }

    if (iconId) {
      getIcon(iconId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iconId, color, mirrored, size, weight]);

  if (icon) {
    return (
      <svg viewBox='0 0 256 256' {...props} {...icon.attributes}>
        {icon?.children ? (parse(icon?.children) as JSX.Element) : null};
      </svg>
    );
  } else {
    return (
      <svg width={size} height={size} viewBox='0 0 256 256' {...props}>
        <rect width='256' height='256' fill='none' />
      </svg>
    );
  }
};

export interface IconStandaloneProps extends PhosphorIconProps {
  iconId: IconId;
}

/**
 * Render custom icons and commonly used icons instantly and reqeust others from the server
 */
export const IconStandalone = ({ iconId, ...props }: IconStandaloneProps) => {
  const iconStyles = { flexShrink: 0 };
  if (iconId && iconId.startsWith('TMH')) {
    const TMHIcon = TMHIcons[iconId];
    // Ignore props on TMH Icon
    if (!TMHIcon) {
      console.error('Unkown TMH icon id provided');
      return <></>;
    }
    return <TMHIcon size={undefined} {...props} />;

    // Load commonly used phosphor icons without the api
  } else if (iconId === 'Car') {
    return <Car {...props} style={iconStyles} />;
  } else if (iconId === 'User') {
    return <User {...props} style={iconStyles} />;
  } else if (iconId === 'Installation') {
    return <Installation {...props} style={iconStyles} />;
  } else if (iconId === 'Search') {
    return <Search color={undefined} {...props} style={iconStyles} />;
  } else {
    return (
      <PhosphorIconWrapper
        iconId={iconId as IconId}
        color={props.color}
        mirrored={props.mirrored}
        size={props.size}
        weight={props.weight}
        {...props}
      />
    );
  }
};

export default IconStandalone;
